import { Box, Text, Heading, Code } from "@chakra-ui/react"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import type { GameStepItem } from "@zrt-ai-office/types/course"
import type { Components } from "react-markdown"

interface GameStepContentProps {
  step: GameStepItem
  stepIndex: number
  totalSteps: number
}

const markdownComponents: Components = {
  h1: ({ children }) => (
    <Heading as="h1" size="2xl" mb="4" mt="6" color="slate.900">
      {children}
    </Heading>
  ),
  h2: ({ children }) => (
    <Heading as="h2" size="xl" mb="3" mt="5" color="slate.800">
      {children}
    </Heading>
  ),
  h3: ({ children }) => (
    <Heading as="h3" size="lg" mb="2" mt="4" color="slate.800">
      {children}
    </Heading>
  ),
  p: ({ children }) => (
    <Text mb="3" lineHeight="1.8" color="slate.700" fontSize="sm">
      {children}
    </Text>
  ),
  ul: ({ children }) => (
    <Box as="ul" pl="5" mb="3" listStyleType="disc" color="slate.700" fontSize="sm">
      {children}
    </Box>
  ),
  ol: ({ children }) => (
    <Box as="ol" pl="5" mb="3" listStyleType="decimal" color="slate.700" fontSize="sm">
      {children}
    </Box>
  ),
  li: ({ children }) => (
    <Box as="li" mb="1" lineHeight="1.8">
      {children}
    </Box>
  ),
  blockquote: ({ children }) => (
    <Box
      as="blockquote"
      borderLeftWidth="4px"
      borderLeftColor="emerald.400"
      pl="4"
      py="2"
      my="3"
      bg="emerald.50"
      borderRadius="md"
      color="slate.700"
      fontSize="sm"
    >
      {children}
    </Box>
  ),
  code: ({ className, children }) => {
    const isBlock = className?.includes("language-")
    if (isBlock) {
      return (
        <Box
          as="pre"
          bg="gray.900"
          color="green.300"
          p="4"
          borderRadius="lg"
          overflowX="auto"
          mb="3"
          fontSize="sm"
          lineHeight="1.6"
        >
          <Code bg="transparent" color="inherit" fontSize="sm" whiteSpace="pre">
            {children}
          </Code>
        </Box>
      )
    }
    return (
      <Code
        bg="emerald.50"
        color="emerald.700"
        px="1.5"
        py="0.5"
        borderRadius="md"
        fontSize="sm"
      >
        {children}
      </Code>
    )
  },
  pre: ({ children }) => <>{children}</>,
  table: ({ children }) => (
    <Box overflowX="auto" mb="3">
      <Box
        as="table"
        w="full"
        fontSize="sm"
        borderWidth="1px"
        borderColor="gray.200"
        borderRadius="lg"
      >
        {children}
      </Box>
    </Box>
  ),
  thead: ({ children }) => (
    <Box as="thead" bg="gray.50">
      {children}
    </Box>
  ),
  th: ({ children }) => (
    <Box
      as="th"
      px="3"
      py="2"
      textAlign="left"
      fontWeight="semibold"
      borderBottomWidth="1px"
      borderColor="gray.200"
    >
      {children}
    </Box>
  ),
  td: ({ children }) => (
    <Box
      as="td"
      px="3"
      py="2"
      borderBottomWidth="1px"
      borderColor="gray.100"
    >
      {children}
    </Box>
  ),
  strong: ({ children }) => (
    <Text as="strong" fontWeight="bold" color="slate.900">
      {children}
    </Text>
  ),
  hr: () => <Box as="hr" my="4" borderColor="gray.200" />,
}

export function GameStepContent({
  step,
  stepIndex,
  totalSteps,
}: GameStepContentProps) {
  return (
    <Box>
      {/* Step label */}
      <Text fontSize="xs" color="slate.400" mb="1" fontWeight="medium">
        第 {stepIndex + 1}/{totalSteps} 步
      </Text>

      {/* Step title */}
      <Heading as="h2" size="lg" color="slate.900" mb="4">
        {step.title}
      </Heading>

      {/* Markdown content */}
      <Box>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={markdownComponents}
        >
          {step.content}
        </ReactMarkdown>
      </Box>
    </Box>
  )
}
